import { Button, CardHeader, InputStatus, Info, Loading, ValidationMessage } from "components";
import { VerifyPhoneNumberForm } from "features";
import { getPhoneNumber, storePreferredMFA, transformPhoneNumber } from "helpers";
import { useUser2FA } from "hooks";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useProfilePrefetch } from "services";
import { rememberDevice, updateMFAPreference } from "aws-amplify/auth";
import { t } from "i18next";

export type ProfileStep = "info" | "action" | "success";

export const Profile2FA = () => {
  const prefetchProfile = useProfilePrefetch("getProfile");
  const { user, has2FA, isLoading, error } = useUser2FA();
  const [step, setStep] = useState<ProfileStep>("info");
  const [phoneNumberSent, setPhoneNumberSent] = useState<boolean>(false);
  const [codeSent, setCodeSent] = useState<boolean>(false);
  const [infoMsg, setInfoMsg] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const [paragraph, setParagraph] = useState<string>("");
  const [mfaError, setMfaError] = useState<string>("");

  const onStatusChange = ({ phoneNumber, code }: { phoneNumber: InputStatus; code: InputStatus }) => {
    setPhoneNumberSent(phoneNumber === "success");
    setCodeSent(code === "success");
  };

  useEffect(() => {
    let message = "";
    if (has2FA) {
      if (step === "info")
        message = `
          ${t("profile.twoFactorAuth.yourCurrentMobilePhoneNumber")} <b>${transformPhoneNumber(getPhoneNumber())}</b> .
        `;
      else if (step === "action")
        message = `
          <div>${t("profile.twoFactorAuth.yourCurrentMobilePhoneNumberIsConnectedWith2FA")}</div>
          <div>${t("profile.twoFactorAuth.youCanChangeThePhoneNumber")}</div>
        `;
      else message = `${t("profile.twoFactorAuth.youHaveSuccessfullyset")}`;
    } else {
      if (step === "info") message = `${t("profile.twoFactorAuth.yourAccountIsNotConnectedWith2FA")}`;
      else if (step === "action")
        message = `
          <div>${t("profile.twoFactorAuth.yourAccountIsCurrentlyNotConnectedWith2FA")}</div>
          <div>${t("profile.twoFactorAuth.connectYourMobilePhoneNumberNow")}</div>
        `;
      else message = `${t("profile.twoFactorAuth.youHaveSuccessfullyset2FA")}`;
    }
    setInfoMsg(message);
  }, [has2FA, step]);

  useEffect(() => {
    let title = "";
    let paragraph = "";
    title = !phoneNumberSent
      ? `${t("profile.twoFactorAuth.setNew2FA")}`
      : `${t("profile.twoFactorAuth.changeYOurMobileYourPhoneNumber")}`;
    if (!codeSent) {
      paragraph = `${t("profile.twoFactorAuth.pleaseUseYour")} ${has2FA ? t("profile.twoFactorAuth.new") : ""} 
      ${t("profile.twoFactorAuth.mobilePhoneNumberToConnectItWit2FA")}.`;
      if (phoneNumberSent)
        paragraph = `Please enter the SMS code you received to connect your ${
          has2FA ? t("profile.twoFactorAuth.new") : ""
        } ${t("profile.twoFactorAuth.mobilePhoneNumberToYour2FA")}`;
    } else {
      paragraph = `${t("profile.twoFactorAuth.selectContinueToSaveChanges")}`;
    }
    setTitle(title);
    setParagraph(paragraph);
  }, [phoneNumberSent, codeSent, has2FA]);

  const onSet2FA = async () => {
    try {
      await updateMFAPreference({ sms: "PREFERRED" });
      await rememberDevice();
      storePreferredMFA("SMS_MFA");
      setStep("success");
      setMfaError("");
    } catch (err: any) {
      console.error("Error setting up 2FA:", err);
      setMfaError(err.message || t("profile.twoFactorAuth.failedToSet2FA"));
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="flex h-full items-center justify-center">
        <ValidationMessage>{error?.message || t("profile.twoFactorAuth.unableToLoadUserData")}</ValidationMessage>
      </div>
    );
  }

  return (
    <div className="flex h-full items-center justify-center">
      <div className="flex flex-col items-center justify-center gap-10 text-center">
        <div className="flex flex-col items-center justify-center gap-4">
          <Info className={step === "success" ? "text-success" : "text-primary"} />
          <h2 className="font-normal" dangerouslySetInnerHTML={{ __html: infoMsg }} />
          {mfaError && <ValidationMessage>{mfaError}</ValidationMessage>}
        </div>
        {step === "info" && (
          <div className="flex flex-col gap-2">
            <Button onClick={() => setStep("action")}>
              {has2FA ? t("profile.twoFactorAuth.change2FANumber") : t("profile.twoFactorAuth.set2FANow")}
            </Button>
            {!has2FA && <Link to="">{t("profile.twoFactorAuth.whatIs2FA")}</Link>}
          </div>
        )}
        {step === "action" && (
          <div className="flex w-[600px] flex-col gap-2 px-4 xs:px-12 sm:px-[70px]">
            <div className="self-center px-20">
              <CardHeader withLanguage={false} title={title} paragraph={paragraph} />
            </div>
            <VerifyPhoneNumberForm
              onContinue={onSet2FA}
              user={user}
              variant="profile"
              onStatusChange={onStatusChange}
              cancel={
                <span onClick={() => setStep("info")} className="cursor-pointer text-xs leading-6 hover:underline">
                  {t("common.cancel")}
                </span>
              }
            />
          </div>
        )}
        {step === "success" && (
          <Link onMouseEnter={() => prefetchProfile()} to="/profile">
            {t("profile.twoFactorAuth.goBackToGeneralInfo")}
          </Link>
        )}
      </div>
    </div>
  );
};
